import { useId, useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock, Check, Phone, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { formatApiErrorMessage } from '@/lib/api-error'
import { shortDate } from '@/features/purchase-orders/action-card-model'
import { useActionItemSource } from '@/features/purchase-orders/action-source'
import { useResolveChase } from './api'
import { addBusinessDays, todayIso } from './model'
import type { ChaseChoice, ChaseOwner } from './types'
import { ComposeTextarea } from '@/components/compose-field'

const DONE_TOASTS: Record<Exclude<ChaseChoice, 'chased' | 'called'>, string> = {
  received: 'Marked received',
  not_needed: 'Marked not needed',
  refunded: 'Refund recorded',
  credited: 'Credit recorded on their account',
}

/**
 * The choices on a "Chase Garland: prepaid invoice" card (action type ``owed_overdue``): we chased
 * them and they now owe it by a new date, it came in, or we no longer need it. A call item ("Call
 * Garland: ship date") is closed by recording the call: what they said goes on the PO's conversation.
 */
export function ChaseActions({
  owner,
  actionId,
  moneyBack = false,
  call = false,
}: {
  owner: ChaseOwner
  actionId: number
  /** A cancelled PO's refund or credit (USA-1471): it came back as a refund, or as a credit on our account. */
  moneyBack?: boolean
  /** A PO's call item (USA-1444): "Record call" replaces "Chased · new due date". */
  call?: boolean
}) {
  const id = useId()
  const resolve = useResolveChase(owner)
  const [dueOn, setDueOn] = useState<string | null>(null)
  const [callNote, setCallNote] = useState('')
  const pendingChoice = resolve.isPending ? resolve.variables?.choice : undefined

  const run = (choice: ChaseChoice, newDue?: string, note?: string) =>
    resolve.mutate(
      { actionId, choice, dueOn: newDue, note },
      {
        onSuccess: () => {
          if ((choice === 'chased' || choice === 'called') && newDue) {
            setDueOn(null)
            setCallNote('')
            toast.success(`${choice === 'called' ? 'Call recorded' : 'Chased'} · now due ${shortDate(newDue)}`)
          } else if (choice !== 'chased' && choice !== 'called') {
            toast.success(DONE_TOASTS[choice])
          }
        },
        onError: (error) => toast.error(formatApiErrorMessage('Not saved', error)),
      },
    )

  const dueDateField = dueOn !== null && (
    <div className="min-w-0 space-y-1 sm:w-44">
      <Label htmlFor={id}>New due date</Label>
      <Input
        id={id}
        type="date"
        value={dueOn}
        min={todayIso()}
        onChange={(event) => setDueOn(event.target.value)}
        autoFocus={!call}
      />
    </div>
  )

  if (dueOn !== null && call) {
    return (
      <form
        className="space-y-2 border-t pt-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (dueOn && callNote.trim()) run('called', dueOn, callNote.trim())
        }}
      >
        <Label htmlFor={`${id}-note`}>What did they say?</Label>
        <ComposeTextarea
          id={`${id}-note`}
          value={callNote}
          onChange={(event) => setCallNote(event.target.value)}
          rows={3}
          className="text-sm"
          autoFocus
        />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          {dueDateField}
          <div className="flex gap-2">
            <LoadingButton
              type="submit"
              size="sm"
              loading={pendingChoice === 'called'}
              loadingText="Recording..."
              disabled={!dueOn || !callNote.trim()}
            >
              <Phone className="size-3.5" aria-hidden="true" /> Record call
            </LoadingButton>
            <Button type="button" size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => setDueOn(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    )
  }

  if (dueOn !== null) {
    return (
      <form
        className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault()
          if (dueOn) run('chased', dueOn)
        }}
      >
        {dueDateField}
        <div className="flex gap-2">
          <LoadingButton type="submit" size="sm" loading={pendingChoice === 'chased'} loadingText="Saving..." disabled={!dueOn}>
            <Check className="size-3.5" aria-hidden="true" /> Save
          </LoadingButton>
          <Button type="button" size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => setDueOn(null)}>
            Cancel
          </Button>
        </div>
      </form>
    )
  }

  return (
    <div className="flex flex-wrap gap-2 border-t pt-3">
      {call ? (
        <Button size="sm" disabled={resolve.isPending} onClick={() => setDueOn(addBusinessDays(5))}>
          <Phone className="size-3.5" aria-hidden="true" /> Record call
        </Button>
      ) : (
        <Button size="sm" disabled={resolve.isPending} onClick={() => setDueOn(addBusinessDays(5))}>
          <CalendarClock className="size-3.5" aria-hidden="true" /> Chased · new due date
        </Button>
      )}
      {moneyBack ? (
        <>
          <LoadingButton
            size="sm"
            variant="outline"
            loading={pendingChoice === 'refunded'}
            loadingText="Saving..."
            disabled={resolve.isPending}
            onClick={() => run('refunded')}
          >
            <Check className="size-3.5" aria-hidden="true" /> Refunded
          </LoadingButton>
          <LoadingButton
            size="sm"
            variant="outline"
            loading={pendingChoice === 'credited'}
            loadingText="Saving..."
            disabled={resolve.isPending}
            onClick={() => run('credited')}
          >
            <Check className="size-3.5" aria-hidden="true" /> Credit on account
          </LoadingButton>
        </>
      ) : (
        <LoadingButton
          size="sm"
          variant="outline"
          loading={pendingChoice === 'received'}
          loadingText="Saving..."
          disabled={resolve.isPending}
          onClick={() => run('received')}
        >
          <Check className="size-3.5" aria-hidden="true" /> Received
        </LoadingButton>
      )}
      <LoadingButton
        size="sm"
        variant="ghost"
        loading={pendingChoice === 'not_needed'}
        loadingText="Saving..."
        disabled={resolve.isPending}
        onClick={() => run('not_needed')}
      >
        <X className="size-3.5" aria-hidden="true" /> No longer needed
      </LoadingButton>
    </div>
  )
}

/** The chase choices on the shared PO / customer action card, for whichever record it belongs to. */
export function ActionCardChaseActions({
  actionId,
  poId,
  moneyBack = false,
  call = false,
}: {
  actionId: number
  poId: string
  moneyBack?: boolean
  call?: boolean
}) {
  const source = useActionItemSource()
  // Manufacturer data items (USA-1474) and warranty items (USA-915) are not chased.
  if (source.kind === 'manufacturer_data' || source.kind === 'warranty_claim') return null
  const owner: ChaseOwner =
    source.kind === 'customer_thread'
      ? { source: 'customer', threadId: source.threadId }
      : { source: 'purchase_order', poUuid: poId }
  // A call is recorded on the PO's conversation, so only a PO's call item offers it.
  return <ChaseActions owner={owner} actionId={actionId} moneyBack={moneyBack} call={call && owner.source === 'purchase_order'} />
}
